import { inject, Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { HttpClient, HttpContext } from "@angular/common/http";
import { GATE_CATALOG_ITEM_TYPES } from "../../../enums/gateCatalogItemTypes.enum";

import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { REFERSH } from "../../../interceptors/cache/cache.interceptor";
import { Reference } from "../../../models/shared/refs";
import { ArticleObject, Article } from "../../../models/api/gateComponents/article";
import { GateCatalogItem } from "../../../models/api/gateComponents/gateCatalogItem";
import { MakeObject, Make } from "../../../models/api/gateComponents/make";
import { ManufacturerObject, Manufacturer } from "../../../models/api/gateComponents/manufacturer";

export const GATE_TYPES: Reference[] = [
  {
    id: "2",
    label: "sectional_door",
  },
  {
    id: "3",
    label: "roller_shutter_door",
  },
  {
    id: "4",
    label: "rolling_grille",
  },
  {
    id: "5",
    label: "folding_door",
  },
  {
    id: "7",
    label: "sliding_door",
  },
  {
    id: "1",
    label: "stacking_door",
  },
  {
    id: "6",
    label: "pvc_high_speed_door",
  },
  {
    id: "8",
    label: "spiral_door",
  },
  {
    id: "9",
    label: "other_vertical_door",
  },
  {
    id: "10",
    label: "other_horizontal_door",
  },
];

@Injectable({
  providedIn: "root",
})
export class GateCatalogService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  /**
   * Retrieves all available component types from the gate catalog.
   * @returns Observable of a list of component type objects, each containing an id and a label.
   */
  public getComponentTypes(): Observable<Reference[]> {
    return this.http.get<Reference[]>(environment.baseUrl + "/componentTypes").pipe(
      this.apiErrorHandler.handle("component_type"),
      map((res) => {
        return res.map((asset) => {
          return { id: asset.id, label: asset.label };
        });
      })
    );
  }

  /**
   * Converts a server response body to a list of gate catalog items.
   * @param itemType the type of gate catalog item to convert to.
   * @param serverResBody the server response body to convert.
   * @returns a list of gate catalog items, or null if the itemType is not recognized.
   */
  private convertToCatalogItem(
    itemType: string,
    serverResBody: ManufacturerObject[] | MakeObject[] | ArticleObject[]
  ): GateCatalogItem[] | null {
    switch (itemType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER: {
        const manufacturers: Manufacturer[] = [];
        (serverResBody as ManufacturerObject[]).forEach((manufacturer) => {
          manufacturers.push(new Manufacturer(manufacturer));
        });
        return manufacturers;
      }
      case GATE_CATALOG_ITEM_TYPES.MAKE: {
        const makes: Make[] = [];
        (serverResBody as MakeObject[]).forEach((make) => {
          makes.push(new Make(make));
        });
        return makes;
      }
      case GATE_CATALOG_ITEM_TYPES.ARTICLE: {
        const articles: Article[] = [];
        (serverResBody as ArticleObject[]).forEach((article) => {
          articles.push(new Article(article));
        });
        return articles;
      }
      default:
        return null;
    }
  }

  public getCatalogItems(
    itemType: GATE_CATALOG_ITEM_TYPES.MANUFACTURER,
    parentId?: string,
    refresh?: boolean
  ): Observable<Manufacturer[] | null>;

  public getCatalogItems(
    itemType: GATE_CATALOG_ITEM_TYPES.MAKE,
    parentId?: string,
    refresh?: boolean
  ): Observable<Make[] | null>;

  public getCatalogItems(
    itemType: GATE_CATALOG_ITEM_TYPES.ARTICLE,
    parentId?: string,
    refresh?: boolean
  ): Observable<Article[] | null>;
  /**
   * Gets a list of gate catalog items of the specified type.
   * If a parentId is specified, only items with that parent id are returned.
   * If refresh is true, the cache is ignored and the list is reloaded from the server.
   * @param itemType the type of gate catalog item to retrieve.
   * @param parentId the id of the parent item.
   * @param refresh whether to ignore the cache and reload the list from the server.
   * @returns an observable of a list of gate catalog items, or null if the itemType is not recognized.
   */
  public getCatalogItems(itemType: string, parentId?: string, refresh = false): Observable<GateCatalogItem[] | null> {
    const params: { parentId?: string; catalogType: string } = {
      catalogType: itemType,
    };
    if (parentId) {
      params.parentId = parentId;
    }
    return this.http
      .get<ManufacturerObject[] | MakeObject[] | ArticleObject[]>(environment.baseUrl + "/gateCatalogItem", {
        params,
        context: new HttpContext().set(REFERSH, refresh),
      })
      .pipe(
        this.apiErrorHandler.handle(`gate_${itemType.toLowerCase()}`),
        map((res) => {
          const catalogItems = this.convertToCatalogItem(itemType, res);
          if (catalogItems) {
            return this.orderByLabel(catalogItems);
          } else {
            return null;
          }
        })
      );
  }

  /**
   * Orders a list of gate catalog items by label.
   * @param catalogItems - Items to order.
   * @returns Ordered items.
   */
  private orderByLabel(catalogItems: GateCatalogItem[]): GateCatalogItem[] {
    return catalogItems.sort((a, b) => {
      if (a?.label && b?.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }

  /**
   * Creates an empty gate catalog item based on the specified type and parent ID.
   *
   * @param itemType - The type of gate catalog item (e.g., MANUFACTURER, MAKE, ARTICLE).
   * @param parentId - Optional ID of the parent item, required for MAKE and ARTICLE.
   * @returns An empty GateCatalogItem of the specified type, or null if the type is unrecognized or parentId is missing for MAKE/ARTICLE.
   */

  public getEmptyGateCatalogItem(itemType: string, parentId?: string): GateCatalogItem | null {
    let gateCatalogItem: GateCatalogItem;
    switch (itemType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        gateCatalogItem = new Manufacturer({ label: "" });
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        if (parentId) {
          gateCatalogItem = new Make({
            label: "",
            manufacturer: { id: parentId },
          });
        } else {
          gateCatalogItem = null;
        }
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        if (parentId) {
          gateCatalogItem = new Article({
            label: "",
            make: { id: parentId },
            enableExchange: true,
            enableInspection: true,
          });
        } else {
          gateCatalogItem = null;
        }
        break;
      default:
        if (parentId) {
          gateCatalogItem = new Article({ label: "", make: { id: parentId } });
        } else {
          gateCatalogItem = null;
        }
        break;
    }
    return gateCatalogItem;
  }

  public getGateTypes(): Observable<Reference[]> {
    // TODO INTEGRATION: replace this with http call GET /gateTypes (WARNING: gateTypes not yet documented in API Docs)
    return of(GATE_TYPES);
  }

  /**
   * Create a new gate catalog item.
   * @param catalogItem The gate catalog item to create.
   * @returns The response body of the POST request.
   */
  public createGateCatalogItem(catalogItem: GateCatalogItem) {
    const type = catalogItem?.gateCatalogItemType;

    return this.http
      .post(environment.baseUrl + "/gateCatalogItem?catalogType=" + type, catalogItem?.values, { observe: "body" })
      .pipe(this.apiErrorHandler.handle(`gate_${type?.toLowerCase()}`));
  }

  /**
   * Edit an existing gate catalog item.
   * @param catalogItem The gate catalog item to edit. The id property is required.
   * @returns The response body of the PUT request.
   */
  public editGateCatalogItem(catalogItem: GateCatalogItem) {
    const type = catalogItem?.gateCatalogItemType;

    return this.http
      .put(environment.baseUrl + `/gateCatalogItem/${catalogItem?.id}?catalogType=` + type, catalogItem?.values, {
        observe: "body",
      })
      .pipe(this.apiErrorHandler.handle(`gate_${type?.toLowerCase()}`));
  }
  /**
   * Deletes an existing gate catalog item.
   *
   * @param catalogItem - The gate catalog item to delete. The id and gateCatalogItemType properties are required.
   * @returns An observable of the server's response to the DELETE request.
   */

  public deleteGateCatalogItem(catalogItem: GateCatalogItem) {
    const type = catalogItem?.gateCatalogItemType;
    const id = catalogItem?.id;
    return this.http
      .delete(environment.baseUrl + `/gateCatalogItem/${id}?catalogType=${type}`)
      .pipe(this.apiErrorHandler.handle(`gate_${type?.toLowerCase()}`));
  }
}
